// Signed-in session: the token issued at login, and the helpers that attach it
// to every backend request. Imported once from index.js.
import axios from 'axios';
import { API_BASE_URL } from './config';

const STORE = window.localStorage;
const TOKEN_KEY = 'token';
const ACCOUNT_KEY = 'user';

export const getToken = () => {
  try {
    return STORE.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const getAccount = () => {
  try {
    return JSON.parse(STORE.getItem(ACCOUNT_KEY));
  } catch {
    return null;
  }
};

export const startSession = (account, token) => {
  STORE.setItem(ACCOUNT_KEY, JSON.stringify(account));
  if (token) STORE.setItem(TOKEN_KEY, token);
  applyToken();
};

export const endSession = () => {
  STORE.removeItem(ACCOUNT_KEY);
  STORE.removeItem(TOKEN_KEY);
  applyToken();
};

const applyToken = () => {
  const token = getToken();
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
};

// A rejected token means the session is over - clear it and return to the login page.
const onUnauthorised = () => {
  endSession();
  if (window.location.pathname !== '/login') window.location.href = '/login';
};

export const installAuth = () => {
  applyToken();

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) onUnauthorised();
      return Promise.reject(error);
    }
  );

  // Screens call fetch() directly too, so add the header there as well.
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const token = getToken();
    if (token && url.startsWith(API_BASE_URL)) {
      const headers = new Headers(init.headers || (typeof input === 'object' ? input.headers : undefined));
      if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
      init = { ...init, headers };
    }
    const response = await nativeFetch(input, init);
    if (response.status === 401 && url.startsWith(API_BASE_URL)) onUnauthorised();
    return response;
  };
};

// Documents are access-controlled; a plain <a href> cannot send the auth header,
// so the token travels on the URL instead.
export const documentLink = (url) => {
  const token = getToken();
  if (!url || !token || !url.includes('/upload/private/')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
};
